import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { PageLayout } from '../../../components/organisms/PageLayout';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import '../../../styles/admin.css';

interface AISettings {
  provider: string;
  model: string;
  temperature: number;
  max_tokens: number;
  available_models: Record<string, string[]>;
}

const PROVIDER_LABELS: Record<string, string> = {
  openai:   'OpenAI',
  gemini:   'Google Gemini',
  deepseek: 'DeepSeek',
  kimi:     'Kimi (Moonshot AI)',
};

const PROVIDER_CONSOLE_URLS: Record<string, { url: string; label: string }> = {
  openai:   { url: 'https://platform.openai.com/', label: 'platform.openai.com' },
  gemini:   { url: 'https://aistudio.google.com/', label: 'aistudio.google.com' },
  deepseek: { url: 'https://platform.deepseek.com/', label: 'platform.deepseek.com' },
  kimi:     { url: 'https://platform.moonshot.ai/', label: 'platform.moonshot.ai' },
};

const AdminAISettings: React.FC = () => {
  const [settings, setSettings] = useState<AISettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4');
  const [temperature, setTemperature] = useState('0.7');
  const [maxTokens, setMaxTokens] = useState('2000');

  useEffect(() => {
    document.title = 'Admin | AI Settings';
    api.get<AISettings>(API_ENDPOINTS.admin.aiSettings)
      .then((data) => {
        setSettings(data);
        setProvider(data.provider);
        setModel(data.model);
        setTemperature(String(data.temperature));
        setMaxTokens(String(data.max_tokens));
      })
      .catch(() => setError('Failed to load AI settings'))
      .finally(() => setLoading(false));
  }, []);

  const availableModels = settings?.available_models[provider] ?? [];

  const handleProviderChange = (newProvider: string) => {
    setProvider(newProvider);
    const models = settings?.available_models[newProvider] ?? [];
    if (models.length > 0) setModel(models[0]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api.patch<AISettings>(API_ENDPOINTS.admin.aiSettings, {
        provider,
        model,
        temperature: parseFloat(temperature),
        max_tokens: parseInt(maxTokens, 10),
      });
      setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const navLinks = [
    { to: '/admin', label: 'Overview', end: true },
    { to: '/admin/users', label: 'Users' },
    { to: '/admin/ai-usage', label: 'AI Usage' },
    { to: '/admin/ai-settings', label: 'AI Settings' },
    { to: '/admin/plans', label: 'Plans' },
    { to: '/admin/blog', label: 'Blog' },
  ];

  return (
    <PageLayout breadcrumbItems={[{ label: 'Admin' }, { label: 'AI Settings' }]}>
      <nav className="admin-nav">
        {navLinks.map(({ to, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}
          >
            {label}
          </NavLink>
        ))}
      </nav>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading...</p>
      ) : (
        <div style={{ display: 'flex', gap: 40, alignItems: 'flex-start' }}>
        <div style={{ maxWidth: 520, flex: '1 1 auto' }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 24 }}>AI Provider Configuration</h2>

          <div className="admin-ai-field">
            <label className="admin-ai-label">Provider</label>
            <select
              className="admin-ai-select"
              value={provider}
              onChange={(e) => handleProviderChange(e.target.value)}
            >
              {Object.entries(PROVIDER_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          <div className="admin-ai-field">
            <label className="admin-ai-label">Model</label>
            <select
              className="admin-ai-select"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {availableModels.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="admin-ai-field">
              <label className="admin-ai-label">Temperature</label>
              <input
                type="number"
                className="admin-ai-input"
                value={temperature}
                min={0}
                max={2}
                step={0.1}
                onChange={(e) => setTemperature(e.target.value)}
              />
              <span className="admin-ai-hint">0.0 – 2.0</span>
            </div>

            <div className="admin-ai-field">
              <label className="admin-ai-label">Max tokens</label>
              <input
                type="number"
                className="admin-ai-input"
                value={maxTokens}
                min={100}
                max={32000}
                step={100}
                onChange={(e) => setMaxTokens(e.target.value)}
              />
              <span className="admin-ai-hint">per request</span>
            </div>
          </div>

          {error && (
            <p style={{ color: '#dc2626', fontSize: 14, marginTop: 12 }}>{error}</p>
          )}

          <button
            className="admin-ai-save-btn"
            onClick={handleSave}
            disabled={saving}
            style={{ marginTop: 24 }}
          >
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save settings'}
          </button>

          {settings && (
            <div style={{ marginTop: 32, padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Active configuration</p>
              <p style={{ fontSize: 13, fontWeight: 500 }}>
                {PROVIDER_LABELS[settings.provider] ?? settings.provider} · {settings.model}
              </p>
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                temp {settings.temperature} · max {settings.max_tokens} tokens
              </p>
            </div>
          )}
        </div>

        <div style={{ flexShrink: 0, padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', minWidth: 220 }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, fontWeight: 500 }}>Provider consoles</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(PROVIDER_CONSOLE_URLS).map(([key, { url, label }]) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#2563eb', textDecoration: 'none' }}
              >
                <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', minWidth: 60 }}>
                  {PROVIDER_LABELS[key]}
                </span>
                <span style={{ color: '#2563eb' }}>{label} →</span>
              </a>
            ))}
          </div>
        </div>

        </div>
      )}
    </PageLayout>
  );
};

export default AdminAISettings;
